/**
 * A site's Health tab (JSS#1158): the cluster health summary, the workloads
 * that need attention, a read-only storage summary, and the event feed.
 *
 * The workload cards are the operator personas' surface from #188 (a
 * workload-level view with state chips, Restart, recent logs, the support
 * bundle and Page on-call), so they stay reachable for an Event or Site
 * Operator here rather than moving behind the Engineer-only Advanced tab.
 * By default the list shows only failing or degraded workloads; "Show all
 * workloads" lists every one, the same cards Advanced → Workloads shows.
 *
 * The storage summary (Site Operator+) keeps the PVC-state read the full
 * Storage panel gave before it moved under Advanced.
 */
import { Role } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import ClusterHealthPanel from './ClusterHealthPanel';
import EventsPanel from './EventsPanel';
import StorageHealthSummary from './StorageHealthSummary';
import WorkloadsPanel from './WorkloadsPanel';
import { useClusterHealth } from './useClusterData';

export default function HealthTab({ siteId }: { siteId: string }) {
  const { data: health, isLoading } = useClusterHealth(siteId);
  return (
    <div className="space-y-6" data-testid="site-health-tab">
      <ClusterHealthPanel health={health} isLoading={isLoading} />
      <WorkloadsPanel siteId={siteId} attentionOnly />
      <RoleGate role={Role.SiteOperator}>
        <StorageHealthSummary siteId={siteId} />
      </RoleGate>
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-silver-muted">Events</h2>
        <EventsPanel siteId={siteId} />
      </section>
    </div>
  );
}
